import PropTypes from "prop-types";
import React from "react";

/**
 * Basic button component that accepts a CSS class and a click handler. If the click handler
 * returns a Promise, the button will automatically be disabled until the Promise resolves.
 *
 * @example
 *   <Button className="btn btn-primary" onClick={() => this.props.dispatch(submitAction())} type="submit">
 *     Submit
 *   </Button>
 */
class Button extends React.Component {
  /**
   * Creates the component with the initial state based on props.
   *
   * @param {object} props Initial props
   */
  constructor(props) {
    super(props);

    this.state = { buttonAttributes: {} };
    if (props.isDisabled) {
      this.state.buttonAttributes.disabled = "disabled";
    }

    this.enableButton = this.enableButton.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  /**
   * Called when the component is unmounted to cancel onClick promise
   * so `this.setState()` is not called on an unmounted component.
   */
  componentWillUnmount() {
    this.unmounted = true;
  }

  /**
   * Re-enable the button when the Promise is resolved/rejected unless the component has been unmounted.
   */
  enableButton() {
    if (!this.unmounted) {
      this.setState({ buttonAttributes: {} });
    }
  }

  /**
   * Wraps the `onClick` prop to handle disabling and re-enabling the
   * button when the click handler passed as a prop returns a Promise.
   */
  onClick() {
    const promise = this.props.onClick();

    if (promise == null || promise.constructor !== Promise) {
      return;
    }

    // Disable the button while the Promise is resolving
    this.setState({ buttonAttributes: { disabled: "disabled" } });

    promise.then(this.enableButton).catch(this.enableButton);
  }

  /**
   * Render the button.
   *
   * @returns {object} The React element
   */
  render() {
    /* eslint-disable react/button-has-type */
    return (
      <button
        className={this.props.className}
        onClick={this.onClick}
        {...this.state.buttonAttributes}
        type={this.props.type}
      >
        {this.props.children}
      </button>
    );
    /* eslint-enable react/button-has-type */
  }
}

Button.propTypes = {
  className: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["button", "reset", "submit"]).isRequired
};

export default Button;
